import React, { useEffect, useState } from "react";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Button } from "react-bootstrap";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";

const statusColor = (status) => {
  if (status === "PAID") return "#03B349";
  if (status === "PENDING") return "#DB9F06";
  return "inherit";
};

/**
 * Joined players with their live standing. Once the tournament ends:
 * 1. Calculate Results saves the final rank and prize for everyone.
 * 2. Pay credits the prizes.
 */
const TournamentPlayersModal = ({ open, onClose, tournament }) => {
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const isPaid = tournament?.status === "PAID";
  // After the protections are applied, so this can be less than the maximum pool.
  const winners = players.filter((p) => p.prizeAmount > 0);
  const prizeTotal = winners.reduce((sum, p) => sum + p.prizeAmount, 0);
  const betTotal = players.reduce((sum, p) => sum + (p.totalBet || 0), 0);
  const winnerTotal = winners.length;
  const hasEnded = tournament?.endDate && new Date(tournament.endDate).getTime() < Date.now();
  // Payout is switched off for now; restore with handlePay and the Pay button below.
  // const pending = players.filter((p) => p.status === "PENDING");
  // const pendingTotal = pending.reduce((sum, p) => sum + (p.prizeAmount || 0), 0);

  useEffect(() => {
    if (open && tournament?.id) {
      fetchPlayers();
    }
  }, [open, tournament?.id]);

  const fetchPlayers = async () => {
    setIsLoading(true);
    const response = await getAllData(`${endPoints.api.TOURNAMENT_ADMIN}/${tournament.id}/players`);
    setIsLoading(false);
    setPlayers(response?.status === "success" ? response.data || [] : []);
  };

  const post = (path, fallbackError) => {
    setIsLoading(true);
    return addData(`${endPoints.api.TOURNAMENT_ADMIN}/${tournament.id}/${path}`, {})
      .then((response) => {
        setIsLoading(false);
        if (response?.data?.status === "success") {
          SuccesToast(response.data.message);
        } else {
          ErrorToast(response?.data?.message || fallbackError);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
        ErrorToast(fallbackError);
      });
  };

  const handleCalculate = async () => {
    await post("calculate", "Unable to calculate results.");
    fetchPlayers();
  };

  /** Fallback if the automatic timer did not run: saves results if needed and pays now. */
  const handleForceSettle = async () => {
    const confirmed = window.confirm(
      "Force settle now? Final results are saved if they are not yet, and every winner is paid immediately without waiting for the payout delay. This cannot be undone."
    );
    if (!confirmed) return;
    await post("settle", "Unable to settle this tournament.");
    fetchPlayers();
  };

  // Payout switched off for now.
  // const handlePay = async () => {
  //   const confirmed = window.confirm(
  //     `Pay ${pending.length} players a total of ${pendingTotal.toFixed(2)} SC? This cannot be undone.`
  //   );
  //   if (!confirmed) return;
  //   await post("pay", "Unable to pay prizes.");
  //   fetchPlayers();
  // };

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        Players & Payout - {tournament?.name} ({players.length} joined)
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <div style={{ marginBottom: "16px", fontSize: "13px", opacity: 0.8 }}>
          {!hasEnded && "Live standings. Results can be calculated once the tournament has ended."}
          {hasEnded &&
            !isPaid &&
            "Tournament ended. Final ranks are saved automatically when it ends, and prizes are paid automatically after the payout delay (5 minutes by default). Calculate Results recalculates them before payout."}
          {isPaid && "All prizes for this tournament have been paid."}
        </div>
        {players.length > 0 && (
          <div style={{ marginBottom: "12px", fontWeight: 600 }}>
            Total bets by players: {betTotal.toFixed(2)} SC. Prizes right now: {prizeTotal.toFixed(2)} SC to{" "}
            {winnerTotal} winners (max pool {tournament?.prizePool ?? "-"} SC)
          </div>
        )}

        <TableContainer component={Paper}>
          <Table size="small" className="c_table">
            <TableHead>
              <TableRow>
                <TableCell align="center">Rank</TableCell>
                <TableCell align="center">User ID</TableCell>
                <TableCell align="center">Name</TableCell>
                <TableCell align="center">Score (SC)</TableCell>
                <TableCell align="center">Total Bet (SC)</TableCell>
                <TableCell align="center">Prize (SC)</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Joined</TableCell>
                <TableCell align="center">Paid On</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={9}>
                    No players have joined yet.
                  </TableCell>
                </TableRow>
              )}
              {players.map((p) => (
                <TableRow key={p.id}>
                  <TableCell align="center">{p.overallRank}</TableCell>
                  <TableCell align="center">{p.idUser}</TableCell>
                  <TableCell align="center">{p.playerName || "-"}</TableCell>
                  <TableCell align="center">{p.playerScore ?? 0}</TableCell>
                  <TableCell align="center">{p.totalBet ?? 0}</TableCell>
                  <TableCell align="center">{p.prizeAmount || 0}</TableCell>
                  <TableCell align="center">
                    <span style={{ color: statusColor(p.status), fontWeight: 700 }}>{p.status}</span>
                  </TableCell>
                  <TableCell align="center">
                    {p.joinedAt ? new Date(p.joinedAt).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell align="center">
                    {p.paidAt ? new Date(p.paidAt).toLocaleString() : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {hasEnded && !isPaid && (
          <Button variant="warning" onClick={handleCalculate}>
            Calculate Results
          </Button>
        )}
        {hasEnded && !isPaid && (
          <Button variant="danger" onClick={handleForceSettle}>
            Force Settle
          </Button>
        )}
        {/* Payout switched off for now.
        {!isPaid && pending.length > 0 && (
          <Button variant="success" onClick={handlePay}>
            Pay {pending.length} Players ({pendingTotal.toFixed(2)} SC)
          </Button>
        )} */}
      </DialogActions>
    </Dialog>
  );
};

export default TournamentPlayersModal;
